/**
 * scoring contracts — owned by work package api-scoring-realtime (modules scoring, contests,
 * leaderboard). Request/response zod schemas of those endpoints so apps/web can import them.
 * Every exported name is prefixed with `Scoring` (no collisions with other packages' contracts).
 *
 * The scorer offline-queue protocol (ScorerOpsRequest/Response, ScorerContext, ScorerSignOffRequest)
 * lives in contracts/caddy.ts and the leaderboard / realtime wire format in contracts/leaderboard.ts
 * and contracts/realtime.ts (both FROZEN).
 */
import { z } from 'zod';
import { DeviceInfoSchema } from '../config/misc';
import {
  ActorType,
  AwardType,
  ContestType,
  EntryStatus,
  ParticipantStatus,
  PlayStatus,
  RoundStatus,
  ScoreAuditAction,
  ScoreSource,
  SignOffMethod,
  SignOffStatus,
} from '../enums';
import { ScorecardSchema } from './leaderboard';

// ---------------------------------------------------------------------------------------------
// primitives
// ---------------------------------------------------------------------------------------------

/** Mandatory free-text reason (admin corrections, unlock, void...). Empty -> 400 REASON_REQUIRED. */
export const ScoringReasonSchema = z.string().max(500);

/** Error codes specific to scoring / contests / leaderboard (besides shared ErrorCode). */
export const ScoringErrorCode = {
  ROUND_NOT_STARTED: 'ROUND_NOT_STARTED',
  INCOMPLETE_CARDS: 'INCOMPLETE_CARDS',
  SIGNOFF_REQUIRED: 'SIGNOFF_REQUIRED',
  SCORECARD_INCOMPLETE: 'SCORECARD_INCOMPLETE',
  SIGN_OFF_LOCKED: 'SIGN_OFF_LOCKED',
  DISPUTE_NOTE_REQUIRED: 'DISPUTE_NOTE_REQUIRED',
  HIDDEN_HOLES_REQUIRED: 'HIDDEN_HOLES_REQUIRED',
  HIDDEN_HOLES_INVALID: 'HIDDEN_HOLES_INVALID',
  NOT_PERIO: 'NOT_PERIO',
  SELF_ENTRY_DISABLED: 'SELF_ENTRY_DISABLED',
  VIEW_UNKNOWN: 'VIEW_UNKNOWN',
  CONTEST_ROUND_MISMATCH: 'CONTEST_ROUND_MISMATCH',
  BIB_NOT_FOUND: 'BIB_NOT_FOUND',
} as const;
export type ScoringErrorCode = (typeof ScoringErrorCode)[keyof typeof ScoringErrorCode];

/** One hole cell as stored on the server. competitorId = participantId or teamId (team formats). */
export const ScoringCellSchema = z.object({
  roundId: z.uuid(),
  competitorId: z.string(),
  holeNumber: z.number(),
  strokes: z.number().nullable(),
  putts: z.number().nullable(),
  pickedUp: z.boolean(),
  source: ScoreSource,
  version: z.number(),
  firstEnteredAt: z.iso.datetime().nullable(),
  updatedAt: z.iso.datetime(),
});
export type ScoringCell = z.infer<typeof ScoringCellSchema>;

// ---------------------------------------------------------------------------------------------
// scorer (caddy / self entry)
// ---------------------------------------------------------------------------------------------

/** GET /scorer/scores?roundId&sinceVersion */
export const ScoringScorerScoresQuerySchema = z.object({
  roundId: z.uuid(),
  /**
   * Realtime version the device last applied. The server always returns every cell of the scorer's
   * groups for the round (<= 4 players x 18 holes) plus the current realtimeVersion; the device
   * replaces its cache when `realtimeVersion` > sinceVersion.
   */
  sinceVersion: z.coerce.number().int().min(0).optional(),
});
export const ScoringScorerScoresResponseSchema = z.object({
  roundId: z.uuid(),
  realtimeVersion: z.number(),
  scores: z.array(ScoringCellSchema),
});
export type ScoringScorerScoresResponse = z.infer<typeof ScoringScorerScoresResponseSchema>;

// ---------------------------------------------------------------------------------------------
// sign-off
// ---------------------------------------------------------------------------------------------

export const ScoringSignOffSchema = z.object({
  id: z.uuid(),
  roundId: z.uuid(),
  participantId: z.uuid(),
  status: SignOffStatus,
  method: SignOffMethod,
  current: z.boolean(),
  scorecardHash: z.string(),
  disputeNote: z.string().nullable(),
  signedAt: z.iso.datetime(),
  invalidatedAt: z.iso.datetime().nullable(),
  supersededAt: z.iso.datetime().nullable(),
});
export type ScoringSignOff = z.infer<typeof ScoringSignOffSchema>;

/** GET /rounds/:roundId/signoffs[?history=1] */
export const ScoringSignOffListQuerySchema = z.object({
  history: z
    .union([z.literal('1'), z.literal('true'), z.literal('0'), z.literal('false')])
    .optional(),
});
export const ScoringSignOffListSchema = z.object({
  roundId: z.uuid(),
  items: z.array(
    z.object({
      participantId: z.uuid(),
      name: z.string(),
      bib: z.number().nullable(),
      groupId: z.uuid().nullable(),
      current: ScoringSignOffSchema.nullable(),
      /** all rows incl. superseded ones, newest first (only with ?history=1) */
      history: z.array(ScoringSignOffSchema).optional(),
    }),
  ),
});
export type ScoringSignOffList = z.infer<typeof ScoringSignOffListSchema>;

/** POST /rounds/:roundId/signoffs — admin records a signed paper card (method ADMIN). */
export const ScoringAdminSignOffRequestSchema = z.object({
  participantId: z.uuid(),
  reason: ScoringReasonSchema,
});

/** POST /player/rounds/:roundId/signoff — disputeNote present -> DISPUTED, else SIGNED. */
export const ScoringPlayerSignOffRequestSchema = z.object({
  confirm: z.boolean(),
  disputeNote: z.string().max(1000).nullable().optional(),
});

// ---------------------------------------------------------------------------------------------
// admin score grid / corrections / audit
// ---------------------------------------------------------------------------------------------

const ScoringGridHoleSchema = z.object({
  number: z.number(),
  par: z.number(),
  strokeIndex: z.number(),
});

/** GET /rounds/:roundId/scores */
export const ScoringGridSchema = z.object({
  roundId: z.uuid(),
  roundNo: z.number(),
  status: RoundStatus,
  teamScoring: z.boolean(),
  courses: z.array(
    z.object({ courseId: z.uuid(), name: z.string(), holes: z.array(ScoringGridHoleSchema) }),
  ),
  competitors: z.array(
    z.object({
      competitorId: z.string(),
      kind: z.enum(['INDIVIDUAL', 'TEAM']),
      name: z.string(),
      bib: z.number().nullable(),
      playStatus: PlayStatus,
      groupId: z.uuid().nullable(),
      groupNumber: z.number().nullable(),
      groupName: z.string().nullable(),
      courseId: z.uuid(),
      startHole: z.number(),
      /** current sign-off (individual competitors only) */
      signOff: z.object({ status: SignOffStatus, method: SignOffMethod }).nullable(),
      /** hole numbers with OPEN anomaly flags (0 = flag not tied to a hole) */
      anomalyHoles: z.array(z.number()),
      scores: z.array(ScoringCellSchema),
    }),
  ),
});
export type ScoringGrid = z.infer<typeof ScoringGridSchema>;

/** PUT /rounds/:roundId/scores — admin correction (ADMIN_CORRECTION, reason required). */
export const ScoringCorrectionRequestSchema = z.object({
  competitorId: z.uuid(),
  holeNumber: z.int().min(1).max(18),
  strokes: z.int().min(1).max(30).nullable(),
  putts: z.int().min(0).max(10).nullable().optional(),
  pickedUp: z.boolean().optional(),
  /** Score.version the admin saw (null = the cell was empty / never written) */
  expectedVersion: z.int().nullable(),
  reason: ScoringReasonSchema,
});
export type ScoringCorrectionRequest = z.infer<typeof ScoringCorrectionRequestSchema>;

export const ScoringCorrectionResponseSchema = z.object({
  score: ScoringCellSchema,
  changed: z.boolean(),
  /** participants whose current sign-off was invalidated by this change */
  signOffsInvalidated: z.array(z.uuid()),
});

/** PUT /rounds/:roundId/scorecards/:competitorId — paper scorecard backup entry. */
export const ScoringPaperCardRequestSchema = z.object({
  holes: z
    .array(
      z.object({
        holeNumber: z.int().min(1).max(18),
        strokes: z.int().min(1).max(30).nullable(),
        putts: z.int().min(0).max(10).nullable().optional(),
        pickedUp: z.boolean().optional(),
      }),
    )
    .min(1)
    .max(18),
  reason: ScoringReasonSchema,
  source: z.literal('PAPER_CARD').default('PAPER_CARD'),
});
export type ScoringPaperCardRequest = z.infer<typeof ScoringPaperCardRequestSchema>;

export const ScoringPaperCardResponseSchema = z.object({
  roundId: z.uuid(),
  competitorId: z.string(),
  changed: z.number(),
  scores: z.array(ScoringCellSchema),
  signOffsInvalidated: z.array(z.uuid()),
});

export const ScoringAuditEntrySchema = z.object({
  id: z.uuid(),
  roundId: z.uuid(),
  roundNo: z.number().nullable(),
  participantId: z.uuid().nullable(),
  teamId: z.uuid().nullable(),
  competitorName: z.string().nullable(),
  holeNumber: z.number(),
  action: ScoreAuditAction,
  oldStrokes: z.number().nullable(),
  newStrokes: z.number().nullable(),
  oldPutts: z.number().nullable(),
  newPutts: z.number().nullable(),
  oldPickedUp: z.boolean().nullable(),
  newPickedUp: z.boolean().nullable(),
  oldVersion: z.number().nullable(),
  newVersion: z.number().nullable(),
  actorType: ActorType,
  actorCaddyId: z.uuid().nullable(),
  actorCaddyCode: z.string().nullable(),
  actorUserId: z.uuid().nullable(),
  actorParticipantId: z.uuid().nullable(),
  reason: z.string().nullable(),
  opId: z.uuid().nullable(),
  clientRecordedAt: z.iso.datetime().nullable(),
  wasOffline: z.boolean(),
  ip: z.string().nullable(),
  userAgent: z.string().nullable(),
  deviceId: z.string().nullable(),
  deviceInfo: DeviceInfoSchema.partial().nullable(),
  /** readable device ("Galaxy A15 · Android 14", "Chrome · Windows") — describeDevice(); additive */
  deviceLabel: z.string().nullable().optional(),
  createdAt: z.iso.datetime(),
});
export type ScoringAuditEntry = z.infer<typeof ScoringAuditEntrySchema>;

/**
 * audit.appended realtime `entry`: the audit row without the raw userAgent / deviceInfo (the feed
 * renders deviceLabel, deviceId and ip) — keeps resume-log frames small (§15 #331 replay cap)
 */
export const ScoringAuditFeedEntrySchema = ScoringAuditEntrySchema.omit({
  userAgent: true,
  deviceInfo: true,
});
export type ScoringAuditFeedEntry = z.infer<typeof ScoringAuditFeedEntrySchema>;

/** GET /rounds/:roundId/scores/:competitorId/:holeNumber/history */
export const ScoringCellHistorySchema = z.object({
  roundId: z.uuid(),
  competitorId: z.string(),
  holeNumber: z.number(),
  current: ScoringCellSchema.nullable(),
  items: z.array(ScoringAuditEntrySchema),
});

/** GET /tournaments/:tournamentId/audit */
export const ScoringAuditQuerySchema = z.object({
  roundId: z.uuid().optional(),
  participantId: z.uuid().optional(),
  teamId: z.uuid().optional(),
  caddyId: z.uuid().optional(),
  holeNumber: z.coerce.number().int().min(1).max(18).optional(),
  action: ScoreAuditAction.optional(),
  from: z.iso.datetime({ offset: true }).optional(),
  to: z.iso.datetime({ offset: true }).optional(),
  cursor: z.uuid().optional(),
  limit: z.coerce.number().int().min(1).max(500).default(100),
});
export const ScoringAuditPageSchema = z.object({
  items: z.array(ScoringAuditEntrySchema),
  nextCursor: z.string().nullable(),
});

// ---------------------------------------------------------------------------------------------
// round lifecycle & hidden holes
// ---------------------------------------------------------------------------------------------

/** POST /rounds/:roundId/{start,complete,lock,unlock} */
export const ScoringRoundTransitionRequestSchema = z.object({
  /** complete: close despite incomplete cards; lock: lock despite missing sign-offs */
  force: z.boolean().optional(),
  /** required for unlock and for force */
  reason: ScoringReasonSchema.nullable().optional(),
});

export const ScoringRoundStateSchema = z.object({
  id: z.uuid(),
  tournamentId: z.uuid(),
  roundNo: z.number(),
  status: RoundStatus,
  startedAt: z.iso.datetime().nullable(),
  completedAt: z.iso.datetime().nullable(),
  lockedAt: z.iso.datetime().nullable(),
  hiddenRevealedAt: z.iso.datetime().nullable(),
});
export type ScoringRoundState = z.infer<typeof ScoringRoundStateSchema>;

/** 409 INCOMPLETE_CARDS details */
export const ScoringIncompleteCardSchema = z.object({
  competitorId: z.string(),
  name: z.string(),
  missingHoles: z.array(z.number()),
});

/** GET /rounds/:roundId/hidden-holes (admin only — secret until the round is locked) */
export const ScoringHiddenHolesSchema = z.object({
  roundId: z.uuid(),
  method: z.string(),
  selection: z.enum(['RANDOM', 'MANUAL']).nullable(),
  revealedAt: z.iso.datetime().nullable(),
  layouts: z.array(
    z.object({
      courseId: z.uuid(),
      courseName: z.string(),
      /** number of hidden holes this layout needs (12/6 for 18 holes, 6/3 for 9-hole rounds) */
      expectedCount: z.number(),
      hiddenHoles: z.array(z.number()),
      seed: z.string().nullable(),
    }),
  ),
});
export type ScoringHiddenHoles = z.infer<typeof ScoringHiddenHolesSchema>;

/** PUT /rounds/:roundId/hidden-holes — MANUAL selection (SCHEDULED rounds only) */
export const ScoringHiddenHolesPutSchema = z.object({
  layouts: z
    .array(
      z.object({
        courseId: z.uuid(),
        hiddenHoles: z.array(z.int().min(1).max(18)).min(1).max(18),
      }),
    )
    .min(1),
});

// ---------------------------------------------------------------------------------------------
// player portal
// ---------------------------------------------------------------------------------------------

/** GET /player/me */
/**
 * New Peria / Peria hidden holes per course layout, exposed ONLY after the reveal (round lock,
 * Round.hiddenRevealedAt set); null/absent before the reveal or when the round has none.
 */
export const RevealedHiddenHolesSchema = z
  .array(z.object({ courseId: z.uuid(), hiddenHoles: z.array(z.number()) }))
  .nullable();
export type RevealedHiddenHoles = z.infer<typeof RevealedHiddenHolesSchema>;

/**
 * A hole of the player's card that changed after the current sign-off (shown while that sign-off
 * is INVALIDATED): the signed value -> the value now, who changed it last and the admin's reason.
 */
export const ScoringCardChangeSchema = z.object({
  holeNumber: z.number(),
  before: z.object({
    strokes: z.number().nullable(),
    putts: z.number().nullable(),
    pickedUp: z.boolean(),
  }),
  after: z.object({
    strokes: z.number().nullable(),
    putts: z.number().nullable(),
    pickedUp: z.boolean(),
  }),
  /** last writer of the hole: ADMIN = organiser / referee correction or paper card */
  by: z.enum(['ADMIN', 'CADDY', 'PLAYER']).nullable(),
  /** the admin's correction reason (caddy edits have none) */
  reason: z.string().nullable(),
  changedAt: z.iso.datetime().nullable(),
});
export type ScoringCardChange = z.infer<typeof ScoringCardChangeSchema>;

export const ScoringPlayerMeSchema = z.object({
  participant: z.object({
    id: z.uuid(),
    fullName: z.string(),
    bibNumber: z.number().nullable(),
    status: ParticipantStatus,
    playStatus: PlayStatus,
    waitlistPosition: z.number().nullable(),
    /** 4-digit code the player gives the caddy to sign on the caddy device */
    signOffCode: z.string().nullable(),
  }),
  tournament: z.object({
    id: z.uuid(),
    name: z.string(),
    startDate: z.string(),
    endDate: z.string(),
    timezone: z.string(),
    status: z.string(),
    formatKind: z.string(),
    /** public leaderboard link (/t/:token), null when none is active */
    publicUrl: z.string().nullable().optional(),
  }),
  /** APPROVED participants only (empty otherwise) */
  rounds: z.array(
    z.object({
      roundId: z.uuid(),
      roundNo: z.number(),
      date: z.string(),
      status: RoundStatus,
      group: z
        .object({
          id: z.uuid(),
          number: z.number(),
          name: z.string().nullable(),
          courseId: z.uuid(),
          startHole: z.number(),
          teeTime: z.string().nullable(),
          /** playing partners (the player included), in group position order */
          members: z
            .array(
              z.object({ participantId: z.uuid(), name: z.string(), bib: z.number().nullable() }),
            )
            .optional(),
        })
        .nullable(),
      /** sign-off is possible (round completed / own card complete) and not yet SIGNED */
      canSignOff: z.boolean(),
      signOff: ScoringSignOffSchema.nullable(),
      /** holes changed since the current sign-off; present only while it is INVALIDATED */
      signOffChanges: z.array(ScoringCardChangeSchema).optional(),
      revealedHiddenHoles: RevealedHiddenHolesSchema.optional(),
    }),
  ),
  /** own scorecard (all holes; same-day Perio handicap hidden until revealed) */
  scorecard: ScorecardSchema.nullable(),
});
export type ScoringPlayerMe = z.infer<typeof ScoringPlayerMeSchema>;

// ---------------------------------------------------------------------------------------------
// contests (니어핀 / 롱기스트)
// ---------------------------------------------------------------------------------------------

/** GET /contests/:contestId/entries — every record incl. VOIDED / SUPERSEDED (admin). */
export const ScoringContestEntrySchema = z.object({
  id: z.uuid(),
  contestId: z.uuid(),
  roundId: z.uuid(),
  roundNo: z.number(),
  participantId: z.uuid(),
  participantName: z.string(),
  bibNumber: z.number(),
  distanceMeters: z.number(),
  measuredAt: z.iso.datetime(),
  status: EntryStatus,
  isLeader: z.boolean(),
  voidReason: z.string().nullable(),
  voidedByType: ActorType.nullable(),
  voidedById: z.uuid().nullable(),
  voidedAt: z.iso.datetime().nullable(),
  supersededById: z.uuid().nullable(),
  supersededAt: z.iso.datetime().nullable(),
  actorType: ActorType,
  enteredByCaddyId: z.uuid().nullable(),
  enteredByCaddyCode: z.string().nullable(),
  enteredByUserId: z.uuid().nullable(),
  deviceId: z.string().nullable(),
  deviceInfo: DeviceInfoSchema.partial().nullable(),
  ip: z.string().nullable(),
  opId: z.uuid().nullable(),
  createdAt: z.iso.datetime(),
  /**
   * place among the VALID entries of this (contest, round): distance, then earlier server
   * createdAt (§15 #32); null for SUPERSEDED / VOIDED entries. Absent from older servers.
   */
  rank: z.number().int().positive().nullable().optional(),
});
export type ScoringContestEntry = z.infer<typeof ScoringContestEntrySchema>;

/** GET /contests/:contestId/entries query: one round only (default: every round) */
export const ScoringContestEntriesQuerySchema = z.object({ roundId: z.uuid().optional() });

/**
 * Entries in ranked order: per round (roundNo ascending), VALID entries by rank, then SUPERSEDED /
 * VOIDED ones newest first.
 */
export const ScoringContestEntriesSchema = z.object({
  contestId: z.uuid(),
  type: ContestType,
  holeNumber: z.number(),
  items: z.array(ScoringContestEntrySchema),
});

/** POST /contests/:contestId/entries — admin record (reason required) */
export const ScoringContestEntryCreateSchema = z.object({
  roundId: z.uuid(),
  bibNumber: z.int().min(0),
  distanceMeters: z.number().positive().max(600),
  measuredAt: z.iso.datetime({ offset: true }),
  reason: ScoringReasonSchema,
});

/** POST /contest-entries/:entryId/void | restore */
export const ScoringReasonRequestSchema = z.object({ reason: ScoringReasonSchema });

export const ScoringContestEntryResultSchema = z.object({
  entry: ScoringContestEntrySchema,
  /** soft warnings (e.g. CONTEST_OUT_OF_RANGE) */
  warnings: z.array(z.string()),
});

// ---------------------------------------------------------------------------------------------
// leaderboard extras
// ---------------------------------------------------------------------------------------------

/** PUT /tournaments/:tournamentId/manual-order — playoff results */
export const ScoringManualOrderRequestSchema = z.object({
  /** "STROKE_GROSS" or a stream key "pub:STROKE_GROSS" / "adm:STROKE_GROSS" (same order for both) */
  viewKey: z.string().min(1).max(64),
  /** competitor ids in final order (empty = clear) */
  competitorIds: z.array(z.uuid()).max(500),
  reason: ScoringReasonSchema,
  /**
   * Holes played in the playoff, in order (e.g. [18, 18, 1]); omitted = keep the recorded holes,
   * [] = clear them. Shown on the results PDF and the players' records ("1–2위 1번 홀 플레이오프").
   */
  playoffHoles: z.array(z.number().int().min(1).max(36)).max(20).optional(),
});
export const ScoringManualOrderResponseSchema = z.object({
  /** includes reserved `playoff:<view>` keys holding playoff holes (manualOrderPlayoffHoles) */
  manualOrder: z.record(z.string(), z.array(z.string())),
});

/** POST /tournaments/:tournamentId/leaderboard/recompute */
export const ScoringRecomputeResponseSchema = z.object({
  ok: z.literal(true),
  computedAt: z.number(),
  version: z.number(),
});

/** GET /public/t/:token/awards */
export const ScoringPublicAwardSchema = z.object({
  awardId: z.uuid(),
  type: AwardType,
  name: z.string(),
  holeNumber: z.number().nullable(),
  roundNo: z.number().nullable(),
  prize: z.string().nullable(),
  winners: z.array(
    z.object({
      winnerId: z.uuid(),
      participantId: z.uuid(),
      name: z.string(),
      bib: z.number().nullable(),
      /** null = not tied to a round / hole (MOST_BIRDIES...) */
      roundNo: z.number().nullable(),
      holeNumber: z.number().nullable(),
      strokes: z.number().nullable(),
      confirmed: z.boolean(),
      detectedAt: z.iso.datetime(),
    }),
  ),
});
export type ScoringPublicAward = z.infer<typeof ScoringPublicAwardSchema>;

/** GET /public/t/:token/groups — pairings, start sheet and Thru */
export const ScoringPublicGroupsSchema = z.object({
  rounds: z.array(
    z.object({
      roundId: z.uuid(),
      roundNo: z.number(),
      date: z.string(),
      status: RoundStatus,
      revealedHiddenHoles: RevealedHiddenHolesSchema.optional(),
      groups: z.array(
        z.object({
          id: z.uuid(),
          number: z.number(),
          name: z.string().nullable(),
          courseId: z.uuid(),
          startHole: z.number(),
          teeTime: z.string().nullable(),
          /** holes entered by the group (max over members, public redaction applied), 'F' = done */
          thru: z.union([z.number(), z.literal('F')]).nullable(),
          members: z.array(
            z.object({
              participantId: z.uuid(),
              name: z.string(),
              bib: z.number().nullable(),
              /** Participant.nationality (ISO code or free text such as "FOREIGN"); absent on older servers */
              nationality: z.string().nullable().optional(),
              thru: z.union([z.number(), z.literal('F')]).nullable(),
            }),
          ),
        }),
      ),
    }),
  ),
});
export type ScoringPublicGroups = z.infer<typeof ScoringPublicGroupsSchema>;
